import { AsyncThunkAction } from '@reduxjs/toolkit';

import { ParaphraseNextStepResponse } from 'api/AICoachGoalsApi';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalPriority, AICoachGoalDecisionType, AICoachGoalType } from 'enums/AICoach';
import { Action, ActionOverrideType } from 'handlers/goalOptimizer';
import { ParaphraseNextStepParams } from 'thunks/createGetParaphraseNextStepThunk';

export const hasPrimaryGoalCompleted = (aiCoachGoals?: AICoachGoalItem[]) =>
  aiCoachGoals && aiCoachGoals.some((g) => g.priority === AICoachGoalPriority.Primary && g.isCompleted);
export const hasSelectedAICoachGoals = (aiCoachGoals?: AICoachGoalItem[]) => aiCoachGoals && aiCoachGoals.length > 0;

const GOAL_TYPES_REQUIRING_MONTHLY_SAVINGS: readonly AICoachGoalType[] = [
  AICoachGoalType.Other,
  AICoachGoalType.SaveForRetirement,
  AICoachGoalType.SaveForSpecificExpense,
  AICoachGoalType.BuildEmergencyFund,
  AICoachGoalType.PayDownStudentLoanDebt,
];

export const goalRequiresMonthlySavings = (goalType: AICoachGoalType) =>
  GOAL_TYPES_REQUIRING_MONTHLY_SAVINGS.includes(goalType);

export const selectedGoalRequiresMonthlySavings = (goals?: AICoachGoalItem[]) =>
  goals?.some((goal) => goalRequiresMonthlySavings(goal.type)) ?? false;

export const hasPrimaryGoal = (aiCoachGoals?: AICoachGoalItem[]) =>
  aiCoachGoals && aiCoachGoals.some((g) => g.priority === AICoachGoalPriority.Primary);

export const hasPayDownCreditCardDebtGoal = (aiCoachGoals?: AICoachGoalItem[]) =>
  aiCoachGoals && aiCoachGoals.some((g) => g.type === AICoachGoalType.PayDownCreditCardDebt);

export const hasPrimaryPayDownCreditCardDebtGoal = (aiCoachGoals?: AICoachGoalItem[]) =>
  Boolean(
    aiCoachGoals?.some(
      (goal) => goal.priority === AICoachGoalPriority.Primary && goal.type === AICoachGoalType.PayDownCreditCardDebt,
    ),
  );

export enum PharaphraseType {
  ConnectSavingsAccount = 'connectSavingsAccount',
}

// Reusable AI Coach Actions
export const AICoachActions = {
  ConnectSavingsAccount: {
    id: 0,
    needsPlaid: true,
    actionText: 'Connect savings account',
  } as Action,
  SkipForNow: {
    id: 1,
    actionText: 'Skip for now',
    actionOverride: ActionOverrideType.ToggleAIChat,
  } as Action,
  CheckMyDashboard: {
    id: 2,
    actionText: 'Finish',
    actionOverride: ActionOverrideType.ToggleAIChat,
  } as Action,
  ConsolidateMyDebt: {
    id: 3,
    actionText: 'Consolidate my debt',
    actionOverride: ActionOverrideType.NavigateToOfferAvailable,
  } as Action,
};

type InternalMessageOptions = {
  text: string;
  actions: Action[];
};

export const getInternalMessageOptions = (
  goal: AICoachGoalDecisionType,
  type: PharaphraseType,
): InternalMessageOptions | null => {
  switch (type) {
    case PharaphraseType.ConnectSavingsAccount:
      return {
        text: `Convert this message into a customer-facing message: You ONLY selected the ${goal} goal, and now you must connect a savings account through Plaid so we can track the goal's progress`,
        actions: [AICoachActions.ConnectSavingsAccount, AICoachActions.SkipForNow],
      };
    default:
      return null;
  }
};

export const DEFAULT_GOAL_SUCCESS_MESSAGE = 'Nice — your primary goal has been updated!';

export interface ParaphraseSuccessMessageParams {
  applicationId: string;
  recommendedPrimaryGoal: AICoachGoalDecisionType;
  dispatchWithUnwrap: <T>(action: AsyncThunkAction<T, any, any>) => Promise<T>;
  paraphraseNextStep: (
    params: ParaphraseNextStepParams,
  ) => AsyncThunkAction<ParaphraseNextStepResponse, ParaphraseNextStepParams, object>;
}

export const getParaphrasedSuccessMessage = async ({
  applicationId,
  recommendedPrimaryGoal,
  dispatchWithUnwrap,
  paraphraseNextStep,
}: ParaphraseSuccessMessageParams): Promise<string> => {
  try {
    const paraphraseResponse = await dispatchWithUnwrap<ParaphraseNextStepResponse>(
      paraphraseNextStep({ applicationId, recommendedPrimaryGoal }),
    );
    if (paraphraseResponse?.paraphrasedText) {
      return paraphraseResponse.paraphrasedText;
    }
  } catch (paraphraseError: unknown) {
    analytics.track('AI Coach - Failed to paraphrase success message', { error: paraphraseError });
  }

  return DEFAULT_GOAL_SUCCESS_MESSAGE;
};

export const getGoalDescription = (goal?: AICoachGoalDecisionType | string) => {
  switch (goal) {
    case AICoachGoalDecisionType.PayOffDebtUsingAvalanche:
      return 'Debt payoff goal';
    case AICoachGoalDecisionType.PayOffDebtUsingSnowball:
      return 'Debt payoff goal';
    case AICoachGoalDecisionType.InvestForRetirement:
      return 'Invest for retirement goal';
    case AICoachGoalDecisionType.ImproveCreditScore:
      return 'Credit score improvement goal';
    case AICoachGoalDecisionType.BuildEmergencySavingsOneMonth:
    case AICoachGoalDecisionType.BuildEmergencySavingsThreeMonths:
    case AICoachGoalDecisionType.BuildEmergencySavingsSixMonths:
    case AICoachGoalDecisionType.BuildEmergencySavings1K:
      return 'Emergency fund goal';
    default:
      return 'Your goal';
  }
};
